import React, { useEffect, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PhotoBook5 from "../assets/img/PhotoBook-8x8.jpg";
import CartService from '../Services/CartService';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../App/Account/ConfirmModal';
import editorStore from '../stores/EditorStore';

interface CartModalProps {
  cartInputs: {
    projectName: string;
    productBasePrice: number;
    productId: number;
    canvasId: number;
    productType: string;
  };
  onClose: () => void;
}

const C = {
  overlay:      'rgba(0,0,0,0.6)',
  panel:        '#13151f',
  panelBorder:  'rgba(255,255,255,0.07)',
  header:       '#1c1e2e',
  surface:      '#1c1e2e',
  surface2:     '#252840',
  surface3:     '#2e3150',
  text1:        '#f0f2ff',
  text2:        '#9da3c8',
  text3:        '#5c6290',
  accent:       '#5b79ff',
  accentHover:  '#4a67f0',
  accentSoft:   'rgba(91,121,255,0.15)',
  accentBorder: 'rgba(91,121,255,0.3)',
  success:      '#22c55e',
  successHover: '#16a34a',
  border:       'rgba(255,255,255,0.07)',
  divider:      'rgba(255,255,255,0.06)',
};

const CartModal: React.FC<CartModalProps> = ({ cartInputs, onClose }) => {
  const [quantity, setQuantity]               = useState(1);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [cartID, setCartID]                   = useState<number | null>(null);
  const [createdQuantity, setCreatedQuantity] = useState(1);
  const [associatedProdId, setAssociatedProdId] = useState(0);
  const [currentImageUrl, setCurrentImageUrl] = useState('');
  const [loading, setLoading]                 = useState(false);
  const navigate = useNavigate();

  const handleIncrement = () => setQuantity(prev => prev + 1);
  const handleDecrement = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

  useEffect(() => {
    const loadImageURL = async () => {
      const currentPageId = editorStore.activePageId;
      const currentImage  = await editorStore.toDataURL({ pageId: currentPageId });
      setCurrentImageUrl(currentImage);
    };
    loadImageURL();
  }, []);

  const ProceedToAddCart = async (canvasId: number, qty: number) => {
    const postData = { cartID: 0, associatedProductID: canvasId, quantity: qty };
    const result   = await CartService.CreateCart(cartInputs.productId, postData);
    if (!result || result === 'Failed to cart the product') {
      toast.error('Failed to add product to cart.', { position: 'top-center' });
      return null;
    }
    if (!result.cartExists) {
      toast.success('Product successfully added to the cart.', { position: 'top-center' });
      navigate('/yourcart', { state: { canvasId }, replace: false });
    }
    return result;
  };

  const handleAddToCart = async () => {
    setLoading(true);
    const result = await ProceedToAddCart(cartInputs.canvasId, quantity);
    setLoading(false);
    if (result?.cartExists === true) {
      setCreatedQuantity(result.quantity);
      setCartID(result.cartID);
      setAssociatedProdId(result.associatedProductID);
      setShowConfirmation(true);
    }
  };

  const handleConfirmAddMore = async () => {
    if (cartID) {
      const updateData = { cartID, associatedProductID: associatedProdId, quantity: createdQuantity + 1 };
      const result = await CartService.UpdateCart(cartInputs.productId, cartInputs.canvasId, updateData);
      if (result) {
        toast.success('Quantity updated successfully.', { position: 'top-center' });
        setShowConfirmation(false);
        navigate('/yourcart', { state: { canvasID: cartInputs.canvasId }, replace: false });
      }
    } else {
      toast.error('Unable to update cart. Cart ID is missing.', { position: 'top-center' });
    }
  };

  const totalPrice = (cartInputs.productBasePrice * quantity).toFixed(2);

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0,
          background: C.overlay,
          backdropFilter: 'blur(4px)',
          WebkitBackdropFilter: 'blur(4px)',
          zIndex: 50,
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0,
        width: '380px',
        background: C.panel,
        borderLeft: `1px solid ${C.panelBorder}`,
        zIndex: 51,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-8px 0 48px rgba(0,0,0,0.6)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: `1px solid ${C.divider}`,
          background: C.header,
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: C.accentSoft,
              border: `1px solid ${C.accentBorder}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
            </div>
            <span style={{ fontSize: 15, fontWeight: 600, color: C.text1, letterSpacing: 0.2 }}>
              Add to Cart
            </span>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: 'none', border: `1px solid ${C.border}`,
              color: C.text3, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 150ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = C.surface2; e.currentTarget.style.color = C.text1; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.text3; }}
          >
            <XMarkIcon style={{ width: 16, height: 16 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* Preview card */}
          <div style={{
            borderRadius: 12,
            border: `1px solid ${C.border}`,
            background: C.surface,
            overflow: 'hidden',
            marginBottom: 20,
          }}>
            <div style={{
              padding: '10px 14px',
              borderBottom: `1px solid ${C.divider}`,
              fontSize: 10, fontWeight: 700, letterSpacing: 0.8,
              textTransform: 'uppercase' as const, color: C.text3,
            }}>
              Preview
            </div>
            <div style={{
              padding: 16,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: C.surface2,
              minHeight: 140,
            }}>
              {cartInputs.productType === 'canvas' ? (
                <img
                  src={currentImageUrl || undefined}
                  alt="Preview"
                  style={{
                    maxWidth: '100%', maxHeight: 160,
                    objectFit: 'contain',
                    borderRadius: 6,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                  }}
                />
              ) : (
                <img
                  src={PhotoBook5}
                  alt="Preview"
                  style={{
                    maxWidth: '100%', maxHeight: 160,
                    objectFit: 'contain',
                    borderRadius: 6,
                    boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                  }}
                />
              )}
            </div>
          </div>

          {/* Project details card */}
          <div style={{
            borderRadius: 12,
            border: `1px solid ${C.border}`,
            background: C.surface,
            overflow: 'hidden',
            marginBottom: 20,
          }}>
            <div style={{ padding: '14px 16px', borderBottom: `1px solid ${C.divider}` }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' as const, color: C.text3, marginBottom: 6 }}>
                Project Name
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text1 }}>
                {cartInputs.projectName}
              </div>
            </div>
            <div style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' as const, color: C.text3, marginBottom: 6 }}>
                Unit Price
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: C.text1, letterSpacing: -0.5 }}>
                ${cartInputs.productBasePrice.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Quantity */}
          <div style={{
            borderRadius: 12,
            border: `1px solid ${C.border}`,
            background: C.surface,
            padding: '14px 16px',
            marginBottom: 20,
          }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' as const, color: C.text3, marginBottom: 12 }}>
              Quantity
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, width: 'fit-content' }}>
              <button
                onClick={handleDecrement}
                style={{
                  width: 36, height: 36, borderRadius: '8px 0 0 8px',
                  background: C.surface2,
                  border: `1px solid ${C.border}`,
                  borderRight: 'none',
                  color: C.text2, fontSize: 18, fontWeight: 300,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 150ms',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = C.surface3; e.currentTarget.style.color = C.text1; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.surface2; e.currentTarget.style.color = C.text2; }}
              >
                −
              </button>
              <div style={{
                width: 52, height: 36,
                background: C.surface3,
                border: `1px solid ${C.accentBorder}`,
                color: C.text1, fontSize: 15, fontWeight: 600,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {quantity}
              </div>
              <button
                onClick={handleIncrement}
                style={{
                  width: 36, height: 36, borderRadius: '0 8px 8px 0',
                  background: C.accent,
                  border: `1px solid ${C.accent}`,
                  borderLeft: 'none',
                  color: '#fff', fontSize: 18, fontWeight: 300,
                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 150ms',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = C.accentHover; }}
                onMouseLeave={e => { e.currentTarget.style.background = C.accent; }}
              >
                +
              </button>
            </div>
          </div>

          {/* Order total */}
          <div style={{
            borderRadius: 12,
            border: `1px solid ${C.accentBorder}`,
            background: C.accentSoft,
            padding: '14px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: C.text2 }}>Order Total</span>
            <span style={{ fontSize: 20, fontWeight: 700, color: C.accent, letterSpacing: -0.5 }}>
              ${totalPrice}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 20px',
          borderTop: `1px solid ${C.divider}`,
          background: C.header,
          display: 'flex', gap: 10,
          flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              flex: 1, height: 40,
              background: 'none',
              border: `1px solid ${C.border}`,
              borderRadius: 8,
              color: C.text2, fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 150ms',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = C.surface2; e.currentTarget.style.color = C.text1; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.text2; e.currentTarget.style.borderColor = C.border; }}
          >
            Cancel
          </button>
          <button
            onClick={handleAddToCart}
            disabled={loading}
            style={{
              flex: 2, height: 40,
              background: loading ? C.accentHover : C.success,
              border: 'none',
              borderRadius: 8,
              color: '#fff', fontSize: 13, fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer',
              transition: 'all 150ms',
              opacity: loading ? 0.7 : 1,
              letterSpacing: 0.3,
            }}
            onMouseEnter={e => { if (!loading) e.currentTarget.style.background = C.successHover; }}
            onMouseLeave={e => { if (!loading) e.currentTarget.style.background = C.success; }}
          >
            {loading ? 'Adding…' : 'Continue'}
          </button>
        </div>
      </div>

      <ConfirmModal
        show={showConfirmation}
        message="Product already exists in your cart. Do you want to add one more quantity?"
        onConfirm={handleConfirmAddMore}
        onCancel={() => setShowConfirmation(false)}
      />
    </>
  );
};

export default CartModal;
